import React from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { navigateToChat } from '../../../NavigateToChat';

interface MenteeProfileModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
}

const MenteeProfileModal: React.FC<MenteeProfileModalProps> = ({ open, onClose, user }) => {
  const p = user?.personal_details || {};
  const extended = user?.extended || {};
  const companyInfo = extended.companyInfo || {};
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);

  const handleMessage = () => {
    const communicationId = user?.communicationId || user?.communication_id || user?.commId || user?.communicationID;
    if (!communicationId) return;
    const listedInCommunity = communityMembers?.find((item) => item?.id?.communicationId === communicationId);
    const isInCommunity = Boolean(listedInCommunity);
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
    onClose();
  };

  const topics: string[] = user?.topics || [];
  const industries: string[] = user?.industries || [];
  const programs: string[] = user?.allGroupsUserBelongsTo || [];

  const name = user?.name || 'Mentee Name';
  const roleCategory = p.category || 'Employee';
  const email = user?.email || '';

  const primaryTopic = topics[0] || 'Topic not set';
  const secondaryTopic = topics[1] || topics[0] || '';
  const primaryIndustry = industries[0] || 'Industry not set';

  const workTitle = primaryTopic;
  const workCompany = primaryIndustry;
  const workArea = companyInfo.markets?.[0] || primaryIndustry;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '600px', md: '900px' },
          maxHeight: '90vh',
          background: 'var(--Fill-Modal-background, #0F0F0F) !important',
          borderRadius: '8px',
          outline: '1px solid var(--Stroke-Modal-border, #444444)',
          outlineOffset: '-1px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* ---------- HEADER (FIGMA) ---------- */}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          gap: 2
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontSize: 18,
            fontFamily: 'Inter !important',
            fontWeight: 700
          }}
        >
          Mentee Profile
        </Typography>

        {/* Close Icon */}
        <Box
          onClick={onClose}
          sx={{
            width: 22,
            height: 22,
            p: 1,
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <CloseOutlinedIcon style={{ color: 'white' }} />
        </Box>
      </Box>

      {/* ---------- BODY WITH SCROLL ---------- */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {/* ----------- PROFILE HEADER CARD ----------- */}
        <Box
          sx={{
            width: '100%',
            pb: 3,
            borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}
        >
          <Box
            component="img"
            src={p.headshot || 'https://placehold.co/100x100'}
            sx={{ width: 100, height: 100, borderRadius: 2 }}
          />

          {/* Name block */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start', // impede stretch das crianças
              gap: 1.5
            }}
          >
            <Typography
              sx={{
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontSize: 18,
                fontWeight: 700
              }}
            >
              {name}
            </Typography>

            <Typography
              sx={{
                color: 'var(--Text-text-secondary, #B6B6B6)',
                fontSize: 16,
                fontWeight: 500
              }}
            >
              {email}
            </Typography>

            {/* Category badge */}
            <Box
              sx={{
                px: 1.5,
                py: '3px',
                borderRadius: '20px',
                outline: '1px solid var(--blue-500, #02A9FE)',
                outlineOffset: '-1px',
                display: 'inline-flex', // garante tamanho mínimo
                width: 'auto'
              }}
            >
              <Typography
                sx={{
                  color: '#FDFDFD',
                  fontSize: 12
                }}
              >
                {roleCategory}
              </Typography>
            </Box>
          </Box>

          {/* CTA Button */}
          <Box
            sx={{
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '6px',
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer'
            }}
            onClick={handleMessage}
          >
            <Typography
              sx={{
                color: '#FDFDFD',
                fontSize: 13,
                fontWeight: 600
              }}
            >
              Message
            </Typography>

            <ForumOutlinedIcon style={{ color: 'white' }} />
          </Box>
        </Box>

        {/* ----------- TWO COLUMN CARDS RESPONSIVE ----------- */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3
          }}
        >
          {/* --------- WORK EXPERIENCE --------- */}
          <CardWrapper title="Work Experience">
            <WorkItem
              title={workTitle}
              company={workCompany}
              years="2025 - Present"
              area={workArea}
              skills={topics.length ? topics.join(' · ') : 'UX/UI Design · Art'}
            />
          </CardWrapper>

          {/* --------- EDUCATION --------- */}
          <CardWrapper title="Education & Skills">
            {industries.length === 0 ? (
              <Typography
                sx={{
                  color: 'var(--Text-text-quaternary, #444)',
                  textAlign: 'center',
                  width: '100%',
                  fontSize: 16,
                  fontWeight: 500
                }}
              >
                No information
              </Typography>
            ) : (
              <WorkItem
                title={primaryIndustry}
                company={secondaryTopic || '—'}
                years="—"
                area={primaryTopic}
                skills={topics.join(', ')}
              />
            )}
          </CardWrapper>
        </Box>

        {/* ----------- MY PROGRAMS ---------- */}
        <CardWrapper title="My Programs">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {programs.length === 0 ? <Tag label="Welcome to Dosen" /> : programs.map((p) => <Tag key={p} label={p} />)}
          </Box>
        </CardWrapper>
      </Box>
    </Dialog>
  );
};

/* ---------------------------------------------------------- */
/* ---------------------- SHARED COMPONENTS ------------------ */
/* ---------------------------------------------------------- */

const CardWrapper = ({ title, children }: any) => (
  <Box
    sx={{
      background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
      borderRadius: 2,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #444',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Typography
        sx={{
          flex: 1,
          color: '#FDFDFD',
          fontSize: 16,
          fontWeight: 700
        }}
      >
        {title}
      </Typography>
    </Box>

    {/* Scroll interno se crescer demais */}
    <Box
      sx={{
        maxHeight: '260px',
        overflowY: 'auto',
        px: 2.5,
        py: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {children}
    </Box>
  </Box>
);

const WorkItem = ({ title, company, years, area, skills }: any) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <WorkOutlineOutlinedIcon style={{ color: 'white' }} />

    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ color: '#868686', fontSize: 14 }}>{company}</Typography>
      <Typography sx={{ color: '#868686', fontSize: 14 }}>{years}</Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>{area}</Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>{skills}</Typography>
    </Box>
  </Box>
);

const Tag = ({ label }: any) => (
  <Box
    sx={{
      px: 1.5,
      py: '3px',
      borderRadius: '20px',
      outline: '1px solid #444',
      outlineOffset: '-1px'
    }}
  >
    <Typography sx={{ color: '#FDFDFD', fontSize: 12 }}>{label}</Typography>
  </Box>
);

export default MenteeProfileModal;
